import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Box } from 'rebass';
import { AsyncCreatableSelect, AsyncSelect } from '../ReactSelect';
import { gql } from '@/modes/company/gql';

export const FETCH_TAGS = gql(`
  query GetTags($where: JSON, $usedOn: TagObjectType, $orderBy: [ColumnOrder]) {
    tags(where: $where, usedOn: $usedOn, limit: 100, orderBy: $orderBy)
  }
`);

type TagPickerProps = {
  value?: string[];
  where?: any;
  placeholder?: string;
  onChange?: (tags: readonly string[]) => void;
  isDisabled?: boolean;
  /**
   * Whether typing an unrecognized value offers a "Create tag ..." option.
   * Defaults to true (editor usage - assigning tags to an object). Set to
   * false for filter-bar usage, where creating a brand new tag makes no
   * sense (nothing could ever match a tag that doesn't exist yet).
   */
  isCreatable?: boolean;
  /**
   * Scopes the suggested tags to those actually used on this type of
   * entity (e.g. 'ASSEMBLY'). Intended for filter-bar usage so the picker
   * never offers a tag that would filter the list down to zero results.
   * Leave unset for editor/assignment usage, where tags from the whole
   * company namespace should be available.
   */
  usedOn?: 'WATER_SERVICE' | 'WATER_SITE' | 'ASSEMBLY' | 'CUSTOMER';
};

const TagPicker = ({
  value,
  where,
  placeholder,
  onChange,
  isDisabled,
  isCreatable = true,
  usedOn,
}: TagPickerProps): JSX.Element => {
  where = where ?? {};
  const client = useApolloClient();

  const selected: { value: string }[] = useMemo(
    () => (value ?? []).map((value) => ({ value })),
    [JSON.stringify(value)]
  );

  const loadOptionsFunc = useCallback(
    async (inputValue: string) => {
      inputValue = (inputValue ?? '').trim();

      const res = await client.query<{ tags: string[] }>({
        query: FETCH_TAGS,
        fetchPolicy: 'network-only',
        variables: {
          where: {
            ...where,
            text: {
              like: '%' + inputValue + '%',
            },
          },
          usedOn,
          limit: null,
          orderBy: [{ col: 'text', dir: 'ASC' }],
        },
      });
      const tags = (res.data.tags ?? []).slice();
      return tags.map((value) => ({ value }));
    },
    [client, JSON.stringify(where), usedOn]
  );

  // Tags can be created elsewhere (e.g. tagging an item in a different
  // picker/modal) while this component stays mounted. react-select's
  // built-in `cacheOptions`/`defaultOptions` caching would otherwise keep
  // showing the list as it was when the menu was first opened, so we
  // manage the default options ourselves and refresh them every time the
  // menu is opened instead of relying on a cache that never invalidates.
  const [defaultOptions, setDefaultOptions] = useState<{ value: string }[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);

  const loadDefaultOptions = useCallback(() => {
    setIsLoading(true);
    loadOptionsFunc('')
      .then(setDefaultOptions)
      .finally(() => setIsLoading(false));
  }, [loadOptionsFunc]);

  // Avoid hitting the server for every mounted picker up front. Only load
  // eagerly if there are already-selected values (so a value that was
  // removed from the list server-side can still be reconciled/validated),
  // otherwise wait until the user actually opens the menu.
  useEffect(() => {
    if ((value ?? []).length > 0) {
      loadDefaultOptions();
    }
  }, [loadDefaultOptions, value]);

  const commonProps = {
    cacheOptions: false,
    isMulti: true as const,
    isDisabled,
    value: selected,
    isLoading,
    loadOptions: loadOptionsFunc,
    defaultOptions,
    onMenuOpen: loadDefaultOptions,
    isClearable: true,
    components: {
      NoOptionsMessage: () => (
        <Box
          p={2}
          sx={{ color: 'secondary', fontSize: 1, textAlign: 'center' }}
        >
          {isCreatable
            ? 'No tags yet — type to create one'
            : 'No matching tags'}
        </Box>
      ),
      LoadingMessage: () => (
        <Box
          p={2}
          sx={{ color: 'secondary', fontSize: 1, textAlign: 'center' }}
        >
          Loading tags...
        </Box>
      ),
    },
    menuPlacement: 'auto' as const,
    onChange: (newValues) => {
      onChange?.(newValues ? newValues.map((value) => value.value) : []);
    },
    isSearchable: true,
    placeholder: placeholder ?? 'Tags',
    getOptionLabel: (value) => value.value,
    getOptionValue: (value) => value.value,
  };

  if (!isCreatable) {
    return <AsyncSelect {...commonProps} />;
  }

  return (
    <AsyncCreatableSelect
      {...commonProps}
      isValidNewOption={(inputValue, _selectValue, options) =>
        inputValue.trim().length > 0 &&
        !(options as { value: string }[]).some(
          (option) =>
            option.value.toLowerCase() === inputValue.trim().toLowerCase()
        )
      }
      getNewOptionData={(inputValue) => ({ value: inputValue.trim() })}
      formatCreateLabel={(inputValue) => `Create tag "${inputValue}"`}
    />
  );
};

export default TagPicker;
